// Monthly Energy Balance Stacked Bar Chart
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { MonthlyBalance } from '@/lib/epseEnergyBalance';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface EPSEMonthlyBalanceProps {
  data: MonthlyBalance[];
}

export function EPSEMonthlyBalance({ data }: EPSEMonthlyBalanceProps) {
  // Transform for chart
  const chartData = data.map(m => ({
    month: m.monthName,
    'Net Inkoop': Math.round(m.grid_kwh / 1000), // Convert to MWh
    'Directe Zon': Math.round(m.pv_kwh / 1000),
    'Uit Batterij': Math.round(m.battery_kwh / 1000),
    'Tekort': Math.round(m.shortfall_kwh / 1000),
  }));

  const yearlyTotals = {
    grid: data.reduce((sum, m) => sum + m.grid_kwh, 0) / 1000,
    pv: data.reduce((sum, m) => sum + m.pv_kwh, 0) / 1000,
    battery: data.reduce((sum, m) => sum + m.battery_kwh, 0) / 1000,
    shortfall: data.reduce((sum, m) => sum + m.shortfall_kwh, 0) / 1000,
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Maandelijkse Energie Balans</CardTitle>
        <CardDescription>
          Verdeling per maand in MWh
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 11 }}
              />
              <YAxis 
                tick={{ fontSize: 10 }}
                label={{ value: 'MWh', angle: -90, position: 'insideLeft', fontSize: 12 }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
                formatter={(value: number) => [`${value} MWh`]}
              />
              <Legend />
              
              <Bar 
                dataKey="Net Inkoop" 
                stackId="a" 
                fill="hsl(var(--chart-1))" 
              />
              <Bar 
                dataKey="Directe Zon" 
                stackId="a" 
                fill="hsl(142 76% 36%)" 
              />
              <Bar 
                dataKey="Uit Batterij" 
                stackId="a" 
                fill="hsl(var(--chart-3))" 
              />
              <Bar 
                dataKey="Tekort" 
                stackId="a" 
                fill="hsl(var(--destructive))" 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        {/* Yearly totals */}
        <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t text-sm">
          <div>
            <p className="text-muted-foreground">Net Inkoop</p>
            <p className="font-bold text-lg">{yearlyTotals.grid.toFixed(0)} MWh</p>
          </div>
          <div>
            <p className="text-muted-foreground">Directe Zon</p>
            <p className="font-bold text-lg text-green-600">{yearlyTotals.pv.toFixed(0)} MWh</p>
          </div>
          <div>
            <p className="text-muted-foreground">Uit Batterij</p>
            <p className="font-bold text-lg">{yearlyTotals.battery.toFixed(0)} MWh</p>
          </div>
          <div>
            <p className="text-muted-foreground">Tekort</p>
            <p className="font-bold text-lg text-destructive">{yearlyTotals.shortfall.toFixed(0)} MWh</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
